import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { useSAL3010_R01Store } from "./Store_SAL3010_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultSAL3010_R01HeaderDto, SAL3010_R01HeaderDto } from "./Dto_SAL3010_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { cellDateComparator, onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL3010_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL3010_R01HeaderDto> | null>;
	defaultSAL3010_R01HeaderRow: SAL3010_R01HeaderDto;
	handleCreateClick: () => void;
}

export const Table_SAL3010_R01 = (props: Props) => {

	const { gridRef, defaultSAL3010_R01HeaderRow, handleCreateClick } = props;

	const { SAL3010_R01List, 
		selectedSAL3010_R01, setSelectedSAL3010_R01, initSelectedSAL3010_R01,
		columnState, setColumnState, 
		editSAL3010_R01,
		SAL3010_R01DetailList, setSAL3010_R01DetailList,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useSAL3010_R01Store();

	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'tradeNo',
		gridData: SAL3010_R01List,
		defaultGridData: defaultSAL3010_R01HeaderRow,
		selectedRow: selectedSAL3010_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		compNm: '사업장',
		tradeDt: '수금일자',
		customNm: '거래처명',
		tradeAmt: '수금합계',
		cashAmt: '현금',
		bankbookAmt: '계좌이체',
		noteAmt: '어음',
		cmsAmt: 'CMS',
		dcAmt: '할인',
		advancesRcp: '선수금',
		remark: '비고',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<SAL3010_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '수금일자', colId: 'tradeDt', field: 'tradeDt', width: 119,
			cellStyle: (p) => getCellStyle(p, 'tradeDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p: { data?: SAL3010_R01HeaderDto }) =>
				p.data?.tradeDt ? formatDate(new Date(p.data.tradeDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 120,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '거래처명', colId: 'customNm', field: 'customNm', width: 120,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '수금합계', colId: 'tradeAmt', field: 'tradeAmt', width: 125,
			cellStyle: (p) => getCellStyle(p, 'tradeAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '현금', colId: 'cashAmt', field: 'cashAmt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'cashAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '계좌이체', colId: 'bankbookAmt', field: 'bankbookAmt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'bankbookAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '어음', colId: 'noteAmt', field: 'noteAmt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'noteAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: 'CMS', colId: 'cmsAmt', field: 'cmsAmt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'cmsAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '할인', colId: 'dcAmt', field: 'dcAmt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'dcAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '선수금', colId: 'advancesRcp', field: 'advancesRcp', width: 100,
			cellStyle: (p) => getCellStyle(p, 'advancesRcp'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 150,
			cellStyle: (p) => getCellStyle(p, 'remark'),
			headerClass: aggridHeaderClass,
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL3010_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL3010_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);


	// 마운트 시 컬럼 상태 복원
	
	const handleRowClick = useCallback((e: RowClickedEvent<SAL3010_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 전표정보와 같은지 확인
		const isSelected = selectedSAL3010_R01?.tradeNo === rowData.tradeNo;
		if (isSelected) {
			setSelectedSAL3010_R01(undefined);
			setDetailScrollTop(null);
			handleCreateClick();
		} else {
			setDetailScrollTop(null);
			setSelectedSAL3010_R01(rowData);
		}
	}, [selectedSAL3010_R01, setSelectedSAL3010_R01, setDetailScrollTop, handleCreateClick]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		tradeDt: SAL3010_R01List.length.toLocaleString() + "건",
		tradeAmt: SAL3010_R01List.reduce((sum, item) => (sum + item.tradeAmt), 0).toLocaleString(),
		cashAmt: SAL3010_R01List.reduce((sum, item) => (sum + (item.cashAmt ?? 0)), 0).toLocaleString(),
		bankbookAmt: SAL3010_R01List.reduce((sum, item) => (sum + (item.bankbookAmt ?? 0)), 0).toLocaleString(),
		noteAmt: SAL3010_R01List.reduce((sum, item) => (sum + (item.noteAmt ?? 0)), 0).toLocaleString(),
		cmsAmt: SAL3010_R01List.reduce((sum, item) => (sum + (item.cmsAmt ?? 0)), 0).toLocaleString(),
		dcAmt: SAL3010_R01List.reduce((sum, item) => (sum + (item.dcAmt ?? 0)), 0).toLocaleString(),
		advancesRcp: SAL3010_R01List.reduce((sum, item) => (sum + (item.advancesRcp ?? 0)), 0).toLocaleString(),
		editable: false,
	}]), [SAL3010_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [SAL3010_R01List, selectedSAL3010_R01]);

	return (
		
		<GridContextWrapper<SAL3010_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSAL3010_R01}
			defaultRowData={defaultSAL3010_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SAL3010_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<SAL3010_R01HeaderDto>({
					idKey: 'tradeNo',
					selectedList: selectedSAL3010_R01 ? [selectedSAL3010_R01.tradeNo] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}    

				// Grid_Select에 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 호버 상태 이벤트를 위한 저장
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
